import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { toCurrency, toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { Order, OrderLine } from "../../../controlKitchenTypes"
import { MonetaryAmount } from "../../../../currency/currencyTypes"

type useExportOrdersToCsvParams = Readonly<{
    data: Order[] | null | undefined
    startDate: DateTime
    endDate: DateTime
}>
export function useExportOrdersToCsv({ data, startDate, endDate }: useExportOrdersToCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })
    const { l10n } = useAppLocalization()
    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-Orders-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    const mapOrderToCsvRow = (order: Order, orderLine: OrderLine) => ({
        From: dateTimeFactory.formatDate(startDate),
        To: dateTimeFactory.formatDate(endDate),
        "Order Id": order.id,
        "PSP reference": order.paymentDetails?.paymentId ?? "",
        "Transaction time": order.created,
        "Transaction type": order.orderType === "REFUND" ? "refund" : "payment",
        "Order status": order.orderStatus ?? "",
        "Product id": orderLine.productId,
        "Product name": orderLine.name,
        Items: orderLine.items,
        [`Item price (${getCurrencyString(orderLine.itemPrice)})`]: getAmountString(orderLine.itemPrice),
        [`Amount (${getCurrencyString(orderLine.price)})`]: getAmountString(orderLine.price),
        "User id": order.customer?.id ?? "",
        "Employee id": order.customer?.employeeId ?? "",
        Initials: order.customer?.initials ?? "",
        Name: order.customer?.displayName ?? "",
        Email: order.customer?.email ?? "",
        "User group id": order.customer?.userGroup?.id ?? "",
        "User group": order.customer?.userGroup?.name ?? "",
        Company: order.accountingCustomerParty?.name ?? "",
        "Payment method": order.paymentDetails?.method
            ? l10n.getStringForEnum("purchases-payment-method", order.paymentDetails?.method ?? "", "")
            : "",
        "Sales location": "",
        Channel: order.salesChannel ?? "",
        "Delivery date": order?.deliveries?.[0]?.deliveryTime,
        Acquirer: order.paymentDetails.acquirer?.name,
    })

    return useCallback(() => {
        const exportRows =
            data && data.length > 0
                ? data.flatMap((order) =>
                      order.orderLines ? order.orderLines.map((orderLine) => mapOrderToCsvRow(order, orderLine)) : [],
                  )
                : [
                      Object.keys(mapOrderToCsvRow({} as Order, {} as OrderLine)).reduce(
                          (acc, key) => ({ ...acc, [key]: "" }),
                          {},
                      ),
                  ]

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, l10n])
}
